import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  CatalogApiResponse,
  CatalogBrowseData,
  CatalogBrowseResponse,
  CatalogCursorPagination,
  CatalogProduct,
  CatalogTreeNode,
  EcomCatalogState,
  EcomDealsQueryParams,
  EcomBrowseQueryParams,
} from "./types";

const API_BASE_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ECOMMERCE_PERFIX,
]
  .filter(Boolean)
  .join("");

const emptyCursorPagination: CatalogCursorPagination = {
  nextCursor: null,
  hasMore: false,
  limit: 24,
  count: 0,
};

const initialState: EcomCatalogState = {
  deals: {
    breadcrumb: [],
    children: [],
    products: [],
    pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
    filters: null,
  },
  browse: {
    breadcrumb: [],
    children: [],
    products: [],
    pagination: emptyCursorPagination,
    filters: null,
  },
  tree: [],
  dealsRail: [],
  loading: false,
  browseLoading: false,
  loadingMore: false,
  treeLoading: false,
  dealsRailLoading: false,
  error: null,
  browseError: null,
};

/**
 * Fetch the Deals & Coupons listing (GET /e-commerce/catalog/deals).
 * Pass `page` (not `cursor`) to get numbered {page,total,totalPages} pagination.
 */
export const fetchDealsBrowse = createAsyncThunk<
  CatalogBrowseData,
  EcomDealsQueryParams,
  { rejectValue: string }
>("ecomCatalog/fetchDealsBrowse", async (params, { rejectWithValue }) => {
  try {
    const response = await axios.get<CatalogApiResponse<CatalogBrowseData>>(
      `${API_BASE_URL}/e-commerce/catalog/deals`,
      { params },
    );
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch deals",
    );
  }
});

/**
 * Category browse page — breadcrumb + children + paginated products + filters
 * in one call. Uses cursor-based pagination for infinite scroll.
 * GET /e-commerce/catalog/categories/browse?superCategory=Electronics&category=Mobiles
 */
export const fetchCategoryBrowse = createAsyncThunk<
  CatalogBrowseResponse,
  EcomBrowseQueryParams,
  { rejectValue: string }
>("ecomCatalog/fetchCategoryBrowse", async (params, { rejectWithValue }) => {
  try {
    const response = await axios.get<CatalogApiResponse<CatalogBrowseResponse>>(
      `${API_BASE_URL}/e-commerce/catalog/categories/browse`,
      { params },
    );
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch category listing",
    );
  }
});

/**
 * Live category tree built from approved catalog products.
 * GET /e-commerce/catalog/categories/tree
 */
export const fetchCategoryTree = createAsyncThunk<
  CatalogTreeNode[],
  void,
  { rejectValue: string }
>("ecomCatalog/fetchCategoryTree", async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get<CatalogApiResponse<CatalogTreeNode[]>>(
      `${API_BASE_URL}/e-commerce/catalog/categories/tree`,
    );
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch category tree",
    );
  }
});

/**
 * Today's Deals rail — products with an active limited-time deal.
 * GET /e-commerce/catalog/deals/rail?sort=discount&limit=4
 */
export const fetchDealsRail = createAsyncThunk<
  { products: CatalogProduct[] },
  EcomBrowseQueryParams,
  { rejectValue: string }
>("ecomCatalog/fetchDealsRail", async (params, { rejectWithValue }) => {
  try {
    const response = await axios.get<
      CatalogApiResponse<{ products: CatalogProduct[]; pagination: any }>
    >(`${API_BASE_URL}/e-commerce/catalog/deals/rail`, { params });
    return {
      products: response.data.data?.products ?? [],
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch deals rail",
    );
  }
});

const ecomCatalogSlice = createSlice({
  name: "ecomCatalog",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.browseError = null;
    },
    /** Reset browse state on category / filter / sort change. */
    resetBrowse: (state) => {
      state.browse = {
        breadcrumb: [],
        children: [],
        products: [],
        pagination: emptyCursorPagination,
        filters: null,
      };
      state.loadingMore = false;
      state.browseError = null;
    },
  },
  extraReducers: (builder) => {
    // ── Deals browse (legacy, numbered pagination) ──────────────────────────
    builder
      .addCase(fetchDealsBrowse.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.deals.products = [];
      })
      .addCase(
        fetchDealsBrowse.fulfilled,
        (state, action: PayloadAction<CatalogBrowseData>) => {
          state.loading = false;
          state.deals = action.payload;
        },
      )
      .addCase(fetchDealsBrowse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch deals";
      });

    // ── Category browse (cursor pagination, infinite scroll) ────────────────
    builder
      .addCase(fetchCategoryBrowse.pending, (state, action) => {
        // cursor present → this is a load-more request, don't clear existing products
        if (action.meta.arg.cursor) {
          state.loadingMore = true;
        } else {
          state.browseLoading = true;
          state.browse.products = [];
          state.browseError = null;
        }
      })
      .addCase(
        fetchCategoryBrowse.fulfilled,
        (state, action) => {
          const payload = action.payload;
          const wasLoadMore = Boolean(action.meta.arg.cursor);

          state.browseLoading = false;
          state.loadingMore = false;
          state.browseError = null;

          if (wasLoadMore) {
            // Infinite scroll — append products, keep breadcrumb/children/filters from first load
            state.browse.products = [...state.browse.products, ...payload.products];
            state.browse.pagination = payload.pagination as CatalogCursorPagination;
          } else {
            // Initial load — replace everything
            state.browse = {
              breadcrumb: payload.breadcrumb,
              children: payload.children,
              products: payload.products,
              pagination: payload.pagination as CatalogCursorPagination,
              filters: payload.filters,
            };
          }
        },
      )
      .addCase(fetchCategoryBrowse.rejected, (state, action) => {
        state.browseLoading = false;
        state.loadingMore = false;
        state.browseError = action.payload || "Failed to fetch category listing";
      });

    // ── Category tree ───────────────────────────────────────────────────────
    builder
      .addCase(fetchCategoryTree.pending, (state) => {
        state.treeLoading = true;
        state.error = null;
      })
      .addCase(
        fetchCategoryTree.fulfilled,
        (state, action: PayloadAction<CatalogTreeNode[]>) => {
          state.treeLoading = false;
          state.tree = action.payload;
        },
      )
      .addCase(fetchCategoryTree.rejected, (state, action) => {
        state.treeLoading = false;
        state.error = action.payload || "Failed to fetch category tree";
      });

    // ── Deals rail ──────────────────────────────────────────────────────────
    builder
      .addCase(fetchDealsRail.pending, (state) => {
        state.dealsRailLoading = true;
        state.error = null;
      })
      .addCase(fetchDealsRail.fulfilled, (state, action) => {
        state.dealsRailLoading = false;
        state.dealsRail = action.payload.products;
      })
      .addCase(fetchDealsRail.rejected, (state, action) => {
        state.dealsRailLoading = false;
        state.error = action.payload || "Failed to fetch deals rail";
      });
  },
});

export const { clearError, resetBrowse } = ecomCatalogSlice.actions;
export default ecomCatalogSlice.reducer;
